import React, { useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import {
  Button,
  Typography,
  Box,
  Card,
  Grid,
  CircularProgress,
} from "@mui/material";
import {
  AmexIcon,
  MasterCardIcon,
  PaypalIcon,
  StripeIcon,
} from "../../Svg/icons";
import { GridCheckCircleIcon } from "@mui/x-data-grid";
import { useBookEventMutation } from "../../Redux/apis/ApiSlice";
import { useNavigate } from "react-router-dom";
import { notification } from "antd";

const PaymentForm = ({ payload, subtotal, event, ticketSummary, canPay }) => {
  const incomingEvent = event;
  let authtoken = localStorage.getItem("authToken");
  const [bookEvent] = useBookEventMutation();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [cardDetails, setCardDetails] = useState({
    name: "",
    expMonth: "",
    expYear: "",
    cvc: "",
  });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const navigate = useNavigate();
  const handleChange = (event) => {
    const { name, value } = event.target;
    setCardDetails({ ...cardDetails, [name]: value });
  };
  const apiUrl = process.env.REACT_APP_API_URL;
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    // Prevent payment if subtotal is less than $0.50
    if (subtotal < 0.5) {
      notification.error({
        message: "Minimum payment amount is $0.50. Please select more tickets.",
      });
      return;
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardElement),
      billing_details: {
        name: cardDetails.name,
      },
    });

    if (error) {
      console.error("Payment error:", error);
    } else {
      try {
        const createdBy = payload.createdBy;
        setLoading(true);
        const response = await fetch(
          `${apiUrl}/api/payment/create-payment-intent`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authtoken}`,
            },
            body: JSON.stringify({
              amount: subtotal,
              currency: "USD",
              createdBy,
            }), // Adjust amount and currency
          }
        );

        // Check if the response is OK
        if (!response.ok) {
          const errorResponse = await response.json(); // Get the error response
          throw new Error(
            errorResponse.error.message || "An unknown error occurred"
          ); // Throw the specific error message
        }

        const { clientSecret } = await response.json();

        const { error: confirmError } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: paymentMethod.id }
        );

        if (confirmError) {
          console.error("Confirmation error:", confirmError);
          throw new Error(
            confirmError.message || "Payment confirmation failed"
          ); // Throw error if payment confirmation fails
        } else {
          // Call the bookEvent API
          const bookingResponse = await bookEvent(payload).unwrap();
          notification.success({
            
            message: "Event booked successfully!", // Success message
          });
          navigate("/ticket-invoice", {
            state: { incomingEvent, ticketSummary, bookingResponse },
          });
        }
      } catch (error) {
        let errorMessage = "An unexpected error occurred. Please try again.";

        // If error is a fetch/HTTP error with a response (for fetch, error is usually just Error)
        if (error && error.message) {
          // Map known Stripe/backend errors to a user-friendly message
          const paymentErrors = [
            "Invalid request to Stripe API.",
            "There was an issue with the card.",
            "Invalid amount",
            "Unsupported currency",
            "An error occurred with the Stripe API.",
            "Network error. Please try again later."
          ];
          if (paymentErrors.includes(error.message)) {
            errorMessage = "Payment method is currently not working, please try again later.";
          } else {
            errorMessage = error.message;
          }
        } else if (error && error.data && error.data.error) {
          // Business logic error (e.g., "Not enough tickets available.")
          errorMessage = error.data.error;
        }

        notification.error({
          message: errorMessage,
        });

        console.error("Error booking event:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePaymentSelect = (paymentMethod) => {
    setSelectedPayment(paymentMethod);
  };

  const PaymentIcon = ({ IconComponent, paymentMethod }) => (
    <Box
      onClick={() => handlePaymentSelect(paymentMethod)}
      sx={{
        position: "relative",
        cursor: "pointer",
        border:
          selectedPayment === paymentMethod
            ? "2px solid #0D99FF"
            : "2px solid transparent",
        borderRadius: "8px",
        transition: "transform 0.2s",
        transform:
          selectedPayment === paymentMethod ? "scale(1.1)" : "scale(1)",
        width: "90px",
        height: "90px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#F5F5F5",
        padding: "8px",
        margin: "4px",
      }}
    >
      <IconComponent />
      {selectedPayment === paymentMethod && (
        <GridCheckCircleIcon
          sx={{
            position: "absolute",
            bottom: "-5px",
            right: "-5px",
            color: "#0D99FF",
          }}
        />
      )}
    </Box>
  );

  return (
    <Grid  xs={12} md={6} pl={{xs: 2.5, md: 5, lg: 5, xl: 5}}>
      <Card sx={{p: 1}}>
        <Typography variant="h6" gutterBottom sx={{p: 2}}>
          Payments
        </Typography>
        {/* {canPay === false && ( */}
          {/* <Typography color="error" sx={{ mb: 2 }}>
            You must complete payment onboarding before you can pay for tickets.
          </Typography> */}
        {/* )} */}
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", justifyContent: "space-between",}}>
            <Typography sx={{ mb: 1, ml: {xl: "1.5rem"} }}>Card Number</Typography>
            <Typography sx={{ mb: 1, mr: {xl: "1.5rem"} }}>Expiry Date</Typography>
          </Box>

          <div style={{ maxWidth: {xs: 200, sm: 500, md: 500, lg: 500, xl: 200}}}>
          <CardElement
            options={{
              style: { base: { background: "#F5F5F5", fontSize: "16px" } },
              hidePostalCode: true,
            }}
            autoComplete="off"
          />
          </div>
          {loading ? (
            <Button
              sx={{
                backgroundColor: "var(--secondary-color)",
                width: "100%",
                justifyContent: "center",
                color: "white",
                borderRadius: "20px",
                marginTop: "5rem",
                "&:hover": {
                  backgroundColor: "var(--primary-color)",
                },
              }}
            >
              Loading ...
            </Button>
          ) : (
            <Button
              type="submit"
              sx={{
                backgroundColor: "var(--secondary-color)",
                width: "100%",
                justifyContent: "center",
                color: "white",
                borderRadius: "20px",
                marginTop: "5rem",
                "&:hover": {
                  backgroundColor: "var(--primary-color)",
                },
              }}
              // disabled={canPay === false}
            >
              Pay Now
            </Button>
          )}
        </form>
      </Card>
    </Grid>
  );
};

export default PaymentForm;
